/**
 * Every colour the portal uses, in one place, each one named.
 *
 * Nothing else may hold a colour literal: Tailwind classes, react-select's
 * emotion objects, inline styles and the stylesheets all read from here, and
 * no-rogue-colours.test.ts fails if a hex or rgb() appears anywhere else in the
 * app.
 *
 * Three groups:
 *   - design tokens — names and values from allenai/design earthranger/DESIGN.md
 *     (regenerate with scripts/sync-design-tokens.js). Reach for these first.
 *   - unlisted — in use but absent from that spec. Each is a candidate to be
 *     replaced by an official token; the prefix is what makes them countable.
 *   - legacy — src/legacy is frozen and still renders with these names. Not for
 *     use in the new tree.
 *
 * CommonJS on purpose: tailwind.config.js runs in node and requires this file,
 * so the palette cannot drift from the classes. src/theme/colors.css mirrors it
 * as CSS variables for the stylesheets; a test keeps the two in step.
 */
const colors = {
  // ── EarthRanger design tokens ───────────────────────────────
  // From DESIGN.md — the names the designers use.
  'amber-1': '#362922',
  'amber-2': '#b83f00',
  'amber-3': '#e76826',
  'amber-4': '#ff803e',
  'amber-5': '#fd9964',
  'amber-6': '#fef7f4',
  black: '#000000',
  'blue-1': '#0056c7',
  'blue-2': '#dde8f8',
  'blue-3': '#76b0ff',
  'blue-4': '#f4f7fd',
  'blue-5': '#2a394d',
  'blue-6': '#002960',
  'blue-7': '#3089ff',
  'brand-accent-green': '#0f865d',
  'brand-acorn-logo': '#008586',
  'green-1': '#006842',
  'green-10': '#78d811',
  'green-11': '#a1e458',
  'green-12': '#0f865d',
  'green-2': '#83af52',
  'green-3': '#00882e',
  'green-4': '#53aa71',
  'green-5': '#004e26',
  'green-6': '#f5f7f3',
  'green-7': '#0fcb8c',
  'green-8': '#53693a',
  'green-9': '#488a00',
  'greyscale-0': '#000000',
  'greyscale-1': '#111111',
  'greyscale-10': '#f3f3f3',
  'greyscale-11': '#f9f9f9',
  'greyscale-2': '#222222',
  'greyscale-3': '#333333',
  'greyscale-4': '#444444',
  'greyscale-5': '#63666a',
  'greyscale-6': '#767676',
  'greyscale-7': '#888b8d',
  'greyscale-8': '#b1b3b3',
  'greyscale-9': '#dddddd',
  'greyscale-off-white': '#faf2e9',
  'indigo-1': '#331878',
  'indigo-2': '#3e35a3',
  'indigo-3': '#5c4fe7',
  'indigo-4': '#57528c',
  'indigo-5': '#867ede',
  'indigo-6': '#f5f5fa',
  'magenta-1': '#8b0f56',
  'magenta-2': '#b62879',
  'magenta-3': '#ed3ea2',
  'magenta-4': '#e76eb3',
  'purple-1': '#71038e',
  'purple-2': '#a100cb',
  'purple-3': '#cc74e6',
  'red-1': '#3e2125',
  'red-2': '#8c1700',
  'red-3': '#d0031b',
  'red-4': '#f23b0e',
  'red-5': '#f06374',
  'red-6': '#fdf2f4',
  'teal-1': '#107283',
  'teal-2': '#62bfd0',
  'teal-3': '#008586',
  'teal-4': '#00958f',
  'teal-5': '#00d0c8',
  'teal-ai2-dark-teal': '#0a3235',
  'teal-ai2-extra-dark-teal': '#032629',
  'teal-t10': '#e7eeee',
  'teal-t100': '#105257',
  'teal-t20': '#cfdcdd',
  'teal-t30': '#b7cbcd',
  'teal-t40': '#9fbabc',
  'teal-t50': '#87a8ab',
  'teal-t60': '#70979a',
  'teal-t70': '#588689',
  'teal-t80': '#407579',
  'teal-t90': '#27545f',
  'ui-background-elevated-component': '#ffffff',
  'ui-background-primary-background': '#ffffff',
  'ui-background-secondary-background': '#faf2e9',
  'ui-background-tertiary-background': '#e7eeee',
  'ui-cta-primary': '#0f865d',
  'ui-cta-secondary-blue': '#cfdcdd',
  'ui-data-categorical-category-1': '#3e35a3',
  'ui-data-categorical-category-10': '#ed3ea2',
  'ui-data-categorical-category-2': '#488a00',
  'ui-data-categorical-category-3': '#00958f',
  'ui-data-categorical-category-4': '#b62879',
  'ui-data-categorical-category-5': '#c6880c',
  'ui-data-categorical-category-6': '#00d0c8',
  'ui-data-categorical-category-7': '#78d811',
  'ui-data-categorical-category-8': '#5c4fe7',
  'ui-data-categorical-category-9': '#f4db20',
  'ui-data-sequential-sequential-1': '#f4db20',
  'ui-data-sequential-sequential-2': '#ffb700',
  'ui-data-sequential-sequential-3': '#ed3ea2',
  'ui-data-sequential-sequential-4': '#a100cb',
  'ui-data-sequential-sequential-5': '#5c4fe7',
  'ui-data-sequential-sequential-6': '#0056c7',
  'ui-data-sequential-sequential-7': '#00882e',
  'ui-data-sequential-sequential-8': '#78d811',
  'ui-data-tracks-track-1': '#3e35a3',
  'ui-data-tracks-track-10': '#f23b0e',
  'ui-data-tracks-track-2': '#b62879',
  'ui-data-tracks-track-3': '#ff803e',
  'ui-data-tracks-track-4': '#ed3ea2',
  'ui-data-tracks-track-5': '#3089ff',
  'ui-data-tracks-track-6': '#8c1700',
  'ui-data-tracks-track-7': '#a100cb',
  'ui-data-tracks-track-8': '#004e26',
  'ui-data-tracks-track-9': '#002960',
  'ui-divider-lines': '#dddddd',
  'ui-event-priority-amber': '#e76826',
  'ui-event-priority-green': '#006842',
  'ui-event-priority-grey': '#888b8d',
  'ui-event-priority-red': '#d0031b',
  'ui-field-outline': '#b1b3b3',
  'ui-icons': '#767676',
  'ui-patrol-status-active': '#3e35a3',
  'ui-patrol-status-done-mobile': '#767676',
  'ui-patrol-status-overdue': '#b62879',
  'ui-patrol-status-scheduled': '#107283',
  'ui-patrol-status-track-line-mobile': '#e76826',
  'ui-status-bg-active-bg': '#f5f5fa',
  'ui-status-bg-draft-bg': '#dde8f8',
  'ui-status-bg-error-bg': '#fdf2f4',
  'ui-status-bg-pending-sync-bg': '#f3f3f3',
  'ui-status-bg-synced-bg': '#f5f7f3',
  'ui-status-bg-warning-bg': '#fef7f4',
  'ui-status-disabled': '#767676',
  'ui-status-error-destructive': '#d0031b',
  'ui-status-ready': '#ffb700',
  'ui-status-running': '#0056c7',
  'ui-status-success': '#0f865d',
  'ui-status-warning': '#e76826',
  'ui-text-disabled': '#767676',
  'ui-text-form-labels': '#105257',
  'ui-text-primary-text': '#0a3235',
  'ui-text-primary-text-reversed': '#ffffff',
  'ui-text-secondary-text': '#105257',
  'ui-text-secondary-text-reversed': '#faf2e9',
  'ui-text-tabs': '#105257',
  white: '#ffffff',
  'yellow-1': '#9d6900',
  'yellow-2': '#c6880c',
  'yellow-3': '#ffb700',
  'yellow-4': '#f4db20',

  // ── Unlisted ────────────────────────────────────────────────
  // No token in DESIGN.md for these values (yet).
  'ui-text-disabled': '#767676',
  'unlisted-blue-basic': '#00309f',
  'unlisted-dark-blue-800': '#071f56',
  'unlisted-dark-purple': '#53389e',
  'unlisted-datepicker-selected-blue': '#3498db',
  'unlisted-dev-stripe-dark': '#7abdcd',
  'unlisted-dev-stripe-light': '#c1dfe6',
  'unlisted-dropdown-selected-text': '#4a4a4a',
  'unlisted-dropdown-text': '#2a2a2a',
  'unlisted-enabled-panel-border': '#ebf4eb',
  'unlisted-form-help-grey': '#4b5563',
  'unlisted-gray-lines': '#cccccc',
  'unlisted-help-button-fill': 'rgba(255, 255, 255, 0.8)',
  'unlisted-json-key-blue': '#b0c4f7',
  'unlisted-json-number-blue': '#6d97f7',
  'unlisted-light-blue': '#f4f8ff',
  'unlisted-light-gray': '#f8f8f8',
  'unlisted-light-orange': '#f67319',
  'unlisted-light-purple': '#f4f0fb',
  'unlisted-local-stripe-dark': '#22c55e',
  'unlisted-local-stripe-light': '#4ade80',
  'unlisted-login-overlay-soft': 'rgba(50, 33, 16, 0.2)',
  'unlisted-login-overlay-strong': 'rgba(50, 33, 16, 0.85)',
  'unlisted-login-panel-grey': '#f2f2f2',
  'unlisted-medium-gray': '#8f8f8f',
  'unlisted-neutral-gray-100': '#d0d2d1',
  'unlisted-neutral-gray-200': '#c4c7c6',
  'unlisted-neutral-gray-300': '#b8bbba',
  'unlisted-neutral-gray-400': '#acb0af',
  'unlisted-neutral-gray-50': '#e7e8e8',
  'unlisted-neutral-gray-500': '#a1a5a3',
  'unlisted-orange': '#d6520e',
  'unlisted-orange-100': '#f9eadb',
  'unlisted-orange-200': '#f2d2b6',
  'unlisted-orange-50': '#fdf6ef',
  'unlisted-orange-600': '#cc572a',
  'unlisted-pale-border-grey': '#bbbbbb',
  'unlisted-purple': '#7b52c7',
  'unlisted-remove-red-bg-hover': '#fbe3e6',
  'unlisted-scrim-15': 'rgba(0, 0, 0, 0.15)',
  'unlisted-scrim-25': 'rgba(0, 0, 0, 0.25)',
  'unlisted-scrim-40': 'rgba(0, 0, 0, 0.4)',
  'unlisted-secondary-gray': '#666666',
  'unlisted-slate-grey': '#6b7280',
  'unlisted-stage-stripe-dark': '#f4c041',
  'unlisted-stage-stripe-light': '#f4e19e',
  'unlisted-very-light-gray': '#fcfcfc',
  'unlisted-very-light-orange': '#f9eee8',
  'unlisted-warning-yellow': '#fdfaf4',

  // ── Legacy tree only ────────────────────────────────────────
  // src/legacy renders with these. Not for the new tree.
  'accent-amber2': '#fd9964',
  'accent-green': '#00882e',
  'accent-green2': '#83af52',
  'accent-red2': '#f06374',
  'blue-basic': '#00309f',
  'brand-text': '#0a3235',
  'brand-text-secondary': '#105257',
  'bright-blue': '#0056c7',
  'bright-green': '#659f2e',
  'connection-green': '#006842',
  'dark-blue-100': '#d4efff',
  'dark-blue-200': '#b1e0ff',
  'dark-blue-300': '#83c7ff',
  'dark-blue-400': '#52a1ff',
  'dark-blue-50': '#e7f6ff',
  'dark-blue-500': '#2b78ff',
  'dark-blue-600': '#074bff',
  'dark-blue-700': '#033dd0',
  'dark-blue-800': '#071f56',
  'dark-blue-900': '#081530',
  'dark-gray': '#333333',
  'dark-green': '#00520c',
  'dark-green-100': '#d7fad1',
  'dark-green-200': '#adf3a7',
  'dark-green-300': '#71e66f',
  'dark-green-400': '#35d237',
  'dark-green-50': '#eefdec',
  'dark-green-500': '#11b819',
  'dark-green-600': '#069510',
  'dark-green-700': '#057710',
  'dark-green-800': '#075e10',
  'dark-green-900': '#043009',
  'dark-purple': '#53389e',
  'divider-lines': '#dddddd',
  'field-outline': '#b1b3b3',
  'gray-lines': '#cccccc',
  'grey-g0': '#000000',
  'grey-g7': '#888b8d',
  'light-blue': '#f4f8ff',
  'light-blue-100': '#dae6f7',
  'light-blue-200': '#b1e0ff',
  'light-blue-300': '#a4c5ec',
  'light-blue-400': '#79a6e1',
  'light-blue-50': '#f2f6fc',
  'light-blue-500': '#5987d8',
  'light-blue-600': '#456dcb',
  'light-blue-700': '#3b5aba',
  'light-blue-800': '#354b98',
  'light-blue-900': '#2f4179',
  'light-blue-hover': '#dde8f8',
  'light-gray': '#f8f8f8',
  'light-gray-button': '#f3f3f3',
  'light-green': '#e8f2e9',
  'light-green-100': '#e5f4d3',
  'light-green-200': '#ceeaac',
  'light-green-300': '#acdc7a',
  'light-green-400': '#8eca51',
  'light-green-50': '#f4faeb',
  'light-green-500': '#659f2e',
  'light-green-600': '#558b25',
  'light-green-700': '#426b20',
  'light-green-800': '#37551f',
  'light-green-900': '#30491e',
  'light-green-hover': '#d4e8d6',
  'light-orange': '#f67319',
  'light-purple': '#f4f0fb',
  'logs-orange': '#d0031b',
  'medium-gray': '#8f8f8f',
  'medium-green-100': '#ecffed',
  'medium-green-200': '#aaffb2',
  'medium-green-300': '#69ff79',
  'medium-green-400': '#21ff35',
  'medium-green-50': '#eefdec',
  'medium-green-500': '#00f20c',
  'medium-green-600': '#00ca06',
  'medium-green-700': '#009e07',
  'medium-green-800': '#007a0b',
  'medium-green-900': '#02650c',
  'neutral-gray-100': '#d0d2d1',
  'neutral-gray-200': '#c4c7c6',
  'neutral-gray-300': '#b8bbba',
  'neutral-gray-400': '#acb0af',
  'neutral-gray-50': '#e7e8e8',
  'neutral-gray-500': '#a1a5a3',
  'neutral-gray-600': '#959998',
  'neutral-gray-700': '#444444',
  'neutral-gray-800': '#333333',
  'neutral-gray-900': '#111111',
  'off-black': '#222222',
  orange: '#d6520e',
  'orange-100': '#f9eadb',
  'orange-200': '#f2d2b6',
  'orange-300': '#eab387',
  'orange-400': '#df844c',
  'orange-50': '#fdf6ef',
  'orange-500': '#da6e35',
  'orange-600': '#cc572a',
  'orange-700': '#a94225',
  'orange-800': '#873625',
  'orange-900': '#6d2f21',
  'pale-green': '#dde8df',
  purple: '#7b52c7',
  'remove-red': '#d0031b',
  'remove-red-bg': '#fdf2f4',
  'remove-red-bg-hover': '#fbe3e6',
  'route-green': '#006842',
  'secondary-background': '#f9f9f9',
  'secondary-gray': '#666666',
  'secondary-text': '#63666a',
  'status-green': '#007a0b',
  'ui-disabled': '#767676',
  'very-light-gray': '#fcfcfc',
  'very-light-orange': '#f9eee8',
  'warning-dark-yellow': '#9d6900',
  'warning-yellow': '#fdfaf4',
  'white-green': '#f7f9f7',
};

module.exports = { colors };
